import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ProjectList from './pages/ProjectList';
import Board from './pages/Board';
import TaskDetail from './pages/TaskDetail';
import Login from './pages/Login';
import Admin from './pages/Admin';
import ErrorBoundary from './components/ErrorBoundary';
import { useLogout, useMe } from './api/auth';

export default function App() {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 px-6 py-4">
        {/* Keyed on the path so navigating away from a crashed view clears
            the boundary instead of staying stuck on the fallback. */}
        <ErrorBoundary resetKey={location.pathname}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Protected><ProjectList /></Protected>} />
            <Route path="/p/:slug" element={<Protected><Board /></Protected>} />
            <Route
              path="/p/:slug/tasks/:taskId"
              element={<Protected><TaskDetail /></Protected>}
            />
            <Route
              path="/admin"
              element={<Protected adminOnly><Admin /></Protected>}
            />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}

/**
 * Auth gate. While we don't know the user yet, render nothing. If we
 * know they're not logged in, bounce to /login. If adminOnly is set,
 * non-admins get a 403-style message.
 */
function Protected({
  children,
  adminOnly,
}: {
  children: React.ReactNode;
  adminOnly?: boolean;
}) {
  const me = useMe();
  if (me.isLoading) {
    return <div className="text-slate-500 text-sm">Loading…</div>;
  }
  if (!me.data) {
    return <Navigate to="/login" replace />;
  }
  if (adminOnly && me.data.role !== 'admin') {
    return (
      <div className="max-w-lg mx-auto bg-amber-50 border border-amber-200 rounded p-4 text-sm">
        <strong>Admins only.</strong> Ask an administrator to grant you access.
      </div>
    );
  }
  return <>{children}</>;
}

function Header() {
  return (
    <header className="border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between">
      <Link to="/" className="font-bold text-lg flex items-center gap-2">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
        Claude Pipeline Panel
      </Link>
      <div className="flex items-center gap-4">
        <ProjectCrumb />
        <CurrentUserChip />
      </div>
    </header>
  );
}

function ProjectCrumb() {
  const params = useParams<{ slug?: string }>();
  if (!params.slug) return null;
  return (
    <div className="text-sm text-slate-500">
      <Link to="/" className="hover:underline">Projects</Link>
      <span className="mx-1">/</span>
      <Link to={`/p/${params.slug}`} className="hover:underline font-mono">
        {params.slug}
      </Link>
    </div>
  );
}

function CurrentUserChip() {
  const me = useMe();
  const logout = useLogout();
  if (!me.data) return null;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-slate-600">
        Hi, <span className="font-medium text-slate-800">{me.data.name}</span>
        {me.data.role === 'admin' && (
          <span className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase tracking-wide">
            admin
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => logout.mutate()}
        disabled={logout.isPending}
        className="text-xs text-slate-500 hover:text-red-700 underline disabled:opacity-50"
        title={logout.error ? (logout.error as Error).message : undefined}
      >
        {logout.isPending ? 'Signing out…' : 'Logout'}
      </button>
      {logout.error && (
        <span className="text-xs text-red-700">
          {(logout.error as Error).message}
        </span>
      )}
    </div>
  );
}
